/* eslint-disable unicorn/prefer-module */
import { useCallback, useEffect, useMemo, useState } from "react";
import { ImCheckboxChecked, ImCheckboxUnchecked } from "react-icons/im";
import cx from "classix";
import { Loader } from "..";
import { getEdificiosOperativos, useCategoriaMoneda } from "../../utils";
import { Header } from "../Header";
import styles from "./CategoriaFastFood.module.scss";

// Datos IPSOS Fast Food
const IPSOS_DATA = {
  consumeFastFood: 0.652, // 65.2%
  noConsume: 0.348, // 34.8%
  frecuenciaConsumo: {
    unaVezMes: { pct: 0.31, factor: 1 }, // 31% - 1 vez/mes
    unaVezSemana: { pct: 0.492, factor: 4 }, // 49.2% - 4 veces/mes
    dosOMasVecesSemana: { pct: 0.19, factor: 10 }, // 19% - ~10 veces/mes
    otro: { pct: 0.01, factor: 0 }, // 1%
  },
  // Marcas principales (participan del simulador)
  marcas: {
    "McDonald's": 0.467,
    "Burger King": 0.14,
    KFC: 0.094,
    "Carl's Jr": 0.09,
    Domino: 0.058,
    Subway: 0.056,
    "Pizza Hut": 0.028,
    "Papa Johns": 0.009,
    Otros: 0.057, // Taragona 2.2% + New Shuchi 1.7% + Juan Maestro 1.1% + Mamut 0.7%
  },
};

// Marcas disponibles para simulación (excluye "Otros")
const MARCAS_SIMULABLES = Object.keys(IPSOS_DATA.marcas).filter((m) => m !== "Otros");

// Opciones de ticket promedio (CLP)
const TICKETS_PROMEDIO = [6000, 8000, 10000, 12000];

// Escenarios de captación
const ESCENARIOS_CAPTACION = [5, 10, 20];

export const FastFood = () => {
  const { formatNumber, formatLocal, convertCLP, pais } = useCategoriaMoneda();
  const [edificios, setEdificios] = useState([]);
  const [loading, setLoading] = useState(true);
  const [checkAll, setCheckAll] = useState(true);
  const [checkedBuildings, setCheckedBuildings] = useState({});
  const [showInventarioModal, setShowInventarioModal] = useState(false);
  const [showOportunidadModal, setShowOportunidadModal] = useState(false);
  const [marcaSeleccionada, setMarcaSeleccionada] = useState("McDonald's");
  const [porcentajeCaptacion, setPorcentajeCaptacion] = useState(10);
  const [ticketPromedio, setTicketPromedio] = useState(8000);
  const [verAnual, setVerAnual] = useState(false);

  useEffect(() => {
    const initialize = async () => {
      try {
        const newEdificios = await getEdificiosOperativos({ pais });
        setEdificios(newEdificios);

        const initialChecked = {};
        newEdificios.forEach(({ id }) => {
          initialChecked[id] = true;
        });
        setCheckedBuildings(initialChecked);
      } finally {
        setLoading(false);
      }
    };

    initialize();
  }, [pais]);

  const toggleCheckAll = useCallback(() => {
    if (!checkAll) {
      const allChecked = {};
      edificios.forEach(({ id }) => {
        allChecked[id] = true;
      });
      setCheckedBuildings(allChecked);
    } else {
      setCheckedBuildings({});
    }
    setCheckAll((prev) => !prev);
  }, [checkAll, edificios]);

  const toggleCheck = useCallback((id) => {
    setCheckedBuildings((prev) => ({
      ...prev,
      [id]: !prev[id],
    }));
  }, []);

  // Calcular totales de edificios seleccionados
  const { totalEjecutivos, cantidadEdificios, totalPantallas, totalEmpresas } = useMemo(() => {
    let ejecutivos = 0;
    let cantidad = 0;
    let pantallas = 0;
    let empresas = 0;

    edificios.forEach(({ id, personas, pantallas: p, espera, totems, cowork, empresaslistado }) => {
      if (checkedBuildings[id]) {
        cantidad++;
        ejecutivos += personas || 0;
        pantallas += (p || 0) + (espera || 0) + (totems || 0) + (cowork || 0);
        empresas += empresaslistado ? empresaslistado.split(",").length : 0;
      }
    });

    return {
      totalEjecutivos: ejecutivos,
      cantidadEdificios: cantidad,
      totalPantallas: pantallas,
      totalEmpresas: empresas,
    };
  }, [edificios, checkedBuildings]);

  // Calcular mercado con IPSOS
  const resultados = useMemo(() => {
    if (!totalEjecutivos) return null;

    // Consumidores de fast food
    const consumidores = Math.round(totalEjecutivos * IPSOS_DATA.consumeFastFood);
    const noConsumidores = Math.round(totalEjecutivos * IPSOS_DATA.noConsume);

    // Calcular consumos mensuales según frecuencia
    const { frecuenciaConsumo, marcas } = IPSOS_DATA;
    const consumosMensuales =
      Math.round(consumidores * frecuenciaConsumo.unaVezMes.pct * frecuenciaConsumo.unaVezMes.factor) +
      Math.round(consumidores * frecuenciaConsumo.unaVezSemana.pct * frecuenciaConsumo.unaVezSemana.factor) +
      Math.round(consumidores * frecuenciaConsumo.dosOMasVecesSemana.pct * frecuenciaConsumo.dosOMasVecesSemana.factor);

    // Mercado mensual
    const mercadoMensual = consumosMensuales * ticketPromedio;
    const mercadoAnual = mercadoMensual * 12;

    // Desglose por frecuencia
    const desgloseFrecuencia = {
      unaVezMes: {
        personas: Math.round(consumidores * frecuenciaConsumo.unaVezMes.pct),
        consumos: Math.round(consumidores * frecuenciaConsumo.unaVezMes.pct * frecuenciaConsumo.unaVezMes.factor),
      },
      unaVezSemana: {
        personas: Math.round(consumidores * frecuenciaConsumo.unaVezSemana.pct),
        consumos: Math.round(consumidores * frecuenciaConsumo.unaVezSemana.pct * frecuenciaConsumo.unaVezSemana.factor),
      },
      dosOMasVecesSemana: {
        personas: Math.round(consumidores * frecuenciaConsumo.dosOMasVecesSemana.pct),
        consumos: Math.round(
          consumidores * frecuenciaConsumo.dosOMasVecesSemana.pct * frecuenciaConsumo.dosOMasVecesSemana.factor
        ),
      },
    };

    // Split por marca
    const mercadoPorMarca = {};
    Object.entries(marcas).forEach(([marca, share]) => {
      mercadoPorMarca[marca] = {
        clientes: Math.round(consumidores * share),
        mercadoMensual: Math.round(mercadoMensual * share),
        mercadoAnual: Math.round(mercadoAnual * share),
      };
    });

    return {
      totalEjecutivos,
      consumidores,
      noConsumidores,
      consumosMensuales,
      consumosAnuales: consumosMensuales * 12,
      mercadoMensual,
      mercadoAnual,
      desgloseFrecuencia,
      mercadoPorMarca,
    };
  }, [totalEjecutivos, ticketPromedio]);

  // Calcular oportunidad por marca
  const calcularOportunidadMarca = useCallback(
    (marca, porcentajeCrecimiento) => {
      if (!resultados) return { clientesGanados: 0, mercadoGanado: 0 };

      const marketShareActual = IPSOS_DATA.marcas[marca] || 0;
      const nuevoMarketShare = marketShareActual * (1 + porcentajeCrecimiento / 100);

      const clientesActuales = Math.round(resultados.consumidores * marketShareActual);
      const clientesNuevos = Math.round(resultados.consumidores * nuevoMarketShare);
      const clientesGanados = clientesNuevos - clientesActuales;

      // Mercado ganado (usando consumo promedio ponderado)
      const consumoPromedioPorPersona = resultados.consumosMensuales / resultados.consumidores;
      const mercadoGanadoMensual = clientesGanados * consumoPromedioPorPersona * ticketPromedio;
      const mercadoGanadoAnual = mercadoGanadoMensual * 12;

      return {
        clientesActuales,
        clientesNuevos,
        clientesGanados,
        marketShareActual: marketShareActual * 100,
        marketShareNuevo: nuevoMarketShare * 100,
        mercadoGanadoMensual,
        mercadoGanadoAnual,
      };
    },
    [resultados, ticketPromedio]
  );

  if (loading || edificios.length === 0) return <Loader />;

  return (
    <div className={styles.fastfoodPage}>
      <Header leftContent={<h1 className={styles.headerTitulo}>Mercado Fast Food</h1>}>
        <div className={styles.inventarioHeader}>
          <div className={styles.inventarioInfo}>
            <span>
              <strong>{cantidadEdificios}</strong> edificios
            </span>
            <span className={styles.separadorVertical}>|</span>
            <span>
              <strong>{formatNumber(totalPantallas)}</strong> pantallas
            </span>
            <span className={styles.separadorVertical}>|</span>
            <span>
              <strong>{formatNumber(totalEjecutivos)}</strong> personas
            </span>
            <span className={styles.separadorVertical}>|</span>
            <span>
              <strong>{formatNumber(totalEmpresas)}</strong> empresas
            </span>
          </div>
          <button type="button" className={styles.inventarioBtn} onClick={() => setShowInventarioModal(true)}>
            Inventario
          </button>
        </div>
      </Header>

      <div className={styles.fastfoodContent}>
        {/* Tabla integrada: consumo + frecuencia */}
        {resultados && (
          <div className={styles.tablaResultadosContainer}>
            <table className={cx("table table-sm", styles.tablaResultados, styles.tablaIntegrada)}>
              <thead className="table-dark">
                <tr>
                  <th className="text-center">No consumen</th>
                  <th className="text-center">Sí consumen</th>
                  <th className="text-center">Frecuencia de consumo</th>
                  <th className="text-center">Personas</th>
                  <th className="text-center">
                    <div className={styles.headerConsumos}>
                      <span>Consumo</span>
                      <div className={styles.toggleContainer}>
                        <span className={cx(styles.toggleLabel, !verAnual && styles.toggleActive)}>mes</span>
                        <label className={styles.toggleSwitch}>
                          <input type="checkbox" checked={verAnual} onChange={(e) => setVerAnual(e.target.checked)} />
                          <span className={styles.toggleSlider} />
                        </label>
                        <span className={cx(styles.toggleLabel, verAnual && styles.toggleActive)}>año</span>
                      </div>
                    </div>
                  </th>
                </tr>
              </thead>
              <tbody>
                <tr>
                  <td rowSpan={3} className={cx("text-center align-middle", styles.celdaNoConsume)}>
                    <div className={styles.celdaContenido}>
                      <span className={styles.badgeIpsos}>{(IPSOS_DATA.noConsume * 100).toFixed(0)}%</span>
                      <div className={styles.valorGrande}>{formatNumber(resultados.noConsumidores)}</div>
                    </div>
                  </td>
                  <td rowSpan={3} className={cx("text-center align-middle", styles.celdaSiConsume)}>
                    <div className={styles.celdaContenido}>
                      <span className={styles.badgeIpsos}>{(IPSOS_DATA.consumeFastFood * 100).toFixed(0)}%</span>
                      <div className={styles.valorGrande}>{formatNumber(resultados.consumidores)}</div>
                    </div>
                  </td>
                  <td className={styles.celdaFrecuencia}>
                    <span className={styles.badgeIpsos}>
                      {(IPSOS_DATA.frecuenciaConsumo.unaVezMes.pct * 100).toFixed(0)}%
                    </span>
                    <span className={styles.frecuenciaTexto}>Una vez por mes</span>
                  </td>
                  <td className="text-center">{formatNumber(resultados.desgloseFrecuencia.unaVezMes.personas)}</td>
                  <td className="text-center">
                    {formatNumber(
                      verAnual
                        ? resultados.desgloseFrecuencia.unaVezMes.consumos * 12
                        : resultados.desgloseFrecuencia.unaVezMes.consumos
                    )}
                  </td>
                </tr>
                <tr>
                  <td className={styles.celdaFrecuencia}>
                    <span className={styles.badgeIpsos}>
                      {(IPSOS_DATA.frecuenciaConsumo.unaVezSemana.pct * 100).toFixed(0)}%
                    </span>
                    <span className={styles.frecuenciaTexto}>Una vez por semana</span>
                  </td>
                  <td className="text-center">{formatNumber(resultados.desgloseFrecuencia.unaVezSemana.personas)}</td>
                  <td className="text-center">
                    {formatNumber(
                      verAnual
                        ? resultados.desgloseFrecuencia.unaVezSemana.consumos * 12
                        : resultados.desgloseFrecuencia.unaVezSemana.consumos
                    )}
                  </td>
                </tr>
                <tr>
                  <td className={styles.celdaFrecuencia}>
                    <span className={styles.badgeIpsos}>
                      {(IPSOS_DATA.frecuenciaConsumo.dosOMasVecesSemana.pct * 100).toFixed(0)}%
                    </span>
                    <span className={styles.frecuenciaTexto}>Dos o más veces por semana</span>
                  </td>
                  <td className="text-center">
                    {formatNumber(resultados.desgloseFrecuencia.dosOMasVecesSemana.personas)}
                  </td>
                  <td className="text-center">
                    {formatNumber(
                      verAnual
                        ? resultados.desgloseFrecuencia.dosOMasVecesSemana.consumos * 12
                        : resultados.desgloseFrecuencia.dosOMasVecesSemana.consumos
                    )}
                  </td>
                </tr>
                <tr className={styles.filaTotalConsumos}>
                  <td colSpan={4} className={styles.celdaVacia} />
                  <td className={cx("text-center", styles.celdaTotalConsumos)}>
                    <strong>
                      {formatNumber(verAnual ? resultados.consumosAnuales : resultados.consumosMensuales)}
                    </strong>
                  </td>
                </tr>
              </tbody>
            </table>

            {/* Ticket + Mercado */}
            <div className={styles.dosBloquesContainer}>
              {/* Ticket promedio */}
              <div className={styles.bloqueTicket}>
                <div className={styles.ticketLabel}>Ticket promedio</div>
                <div className={styles.ticketBox}>
                  <div className={styles.ticketOpciones}>
                    {TICKETS_PROMEDIO.map((t) => (
                      <label key={t} className={styles.ticketRadio}>
                        <input
                          type="radio"
                          name="ticketPromedio"
                          checked={ticketPromedio === t}
                          onChange={() => setTicketPromedio(t)}
                        />
                        <span>{formatLocal(convertCLP(t))}</span>
                      </label>
                    ))}
                  </div>
                </div>
              </div>

              {/* Mercado */}
              <div className={styles.bloqueMercado}>
                <div className={styles.mercadoLabel}>Mercado {verAnual ? "anual" : "mensual"}</div>
                <div className={styles.mercadoValorGrande}>
                  {formatLocal(convertCLP(verAnual ? resultados.mercadoAnual : resultados.mercadoMensual))}
                </div>
              </div>
            </div>

            {/* Botón oportunidad por marca */}
            <div className={styles.oportunidadBtnContainer}>
              <button className={styles.oportunidadBtn} onClick={() => setShowOportunidadModal(true)}>
                Ver oportunidad por marca
              </button>
            </div>

            {/* Disclaimer */}
            <div className={styles.disclaimer}>
              <p>
                <strong>Oportunidades adicionales para marcas Fast Food:</strong>
              </p>
              <p>
                • <strong>{formatNumber(resultados.noConsumidores)}</strong> personas no consumen Fast Food - potencial
                de captación.
              </p>
              <p>• Delivery: 77% de la audiencia utiliza APP de delivery con alta frecuencia semanal.</p>
              <p>• Segmento familiar: 33% tiene hijos entre 5 y 10 años.</p>
              <p>
                • Visitantes de los edificios que también consumen Fast Food.
                <a href="/listado-empresas" target="_blank" rel="noopener noreferrer">
                  → Revisar empresas por edificio
                </a>
              </p>
            </div>
          </div>
        )}
      </div>

      {/* Modal de Inventario */}
      {showInventarioModal && (
        <div className={styles.modalOverlay} onClick={() => setShowInventarioModal(false)}>
          <div className={styles.modalContent} onClick={(e) => e.stopPropagation()}>
            <div className={styles.modalHeader}>
              <h3>Seleccionar Edificios</h3>
              <button className={styles.modalClose} onClick={() => setShowInventarioModal(false)}>
                ×
              </button>
            </div>
            <div className={styles.modalBody}>
              <div className={styles.modalSummary}>
                <span>
                  <strong>{cantidadEdificios}</strong> edificios seleccionados
                </span>
                <span>
                  <strong>{formatNumber(totalEjecutivos)}</strong> ejecutivos
                </span>
                <button className={styles.toggleAllBtn} onClick={toggleCheckAll}>
                  {checkAll ? "Deseleccionar todos" : "Seleccionar todos"}
                </button>
              </div>
              <table className="table table-striped table-hover table-sm">
                <thead className="table-dark">
                  <tr>
                    <th scope="col" className="text-center" style={{ width: "50px" }} />
                    <th scope="col" className="text-start">
                      EDIFICIO
                    </th>
                    <th scope="col" className="text-center">
                      UBICACIÓN
                    </th>
                    <th scope="col" className="text-center">
                      EJECUTIVOS
                    </th>
                  </tr>
                </thead>
                <tbody>
                  {edificios.map(({ created_at, edificio, id, ubicacion, personas }) => (
                    <tr
                      key={created_at}
                      onClick={() => toggleCheck(id)}
                      className={cx(checkedBuildings[id] && "table-success")}
                      style={{ cursor: "pointer" }}
                    >
                      <td className="text-center">
                        {checkedBuildings[id] ? <ImCheckboxChecked size={16} /> : <ImCheckboxUnchecked size={16} />}
                      </td>
                      <td className="text-center">{edificio.toUpperCase()}</td>
                      <td className="text-center">{ubicacion}</td>
                      <td className="text-center">{personas ? formatNumber(personas) : "-"}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
            <div className={styles.modalFooter}>
              <button className={styles.modalBtn} onClick={() => setShowInventarioModal(false)}>
                Aplicar selección
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Modal de Oportunidad por Marca */}
      {showOportunidadModal && (
        <div className={styles.modalOverlay} onClick={() => setShowOportunidadModal(false)}>
          <div className={cx(styles.modalContent, styles.modalOportunidad)} onClick={(e) => e.stopPropagation()}>
            <div className={styles.modalHeader}>
              <h3>Simulador de Oportunidad por Marca</h3>
              <button className={styles.modalClose} onClick={() => setShowOportunidadModal(false)}>
                ×
              </button>
            </div>
            <div className={styles.modalBody}>
              <div className={styles.marcaSelector}>
                <label>Selecciona tu marca:</label>
                <select
                  value={marcaSeleccionada}
                  onChange={(e) => setMarcaSeleccionada(e.target.value)}
                  className="form-select"
                >
                  {MARCAS_SIMULABLES.map((marca) => (
                    <option key={marca} value={marca}>
                      {marca}
                    </option>
                  ))}
                </select>
              </div>

              <div className={styles.marketShareActual}>
                <h4>Market Share Actual (IPSOS)</h4>
                <div className={styles.marcasGrid}>
                  {Object.entries(IPSOS_DATA.marcas).map(([marca, share]) => (
                    <div
                      key={marca}
                      className={cx(
                        styles.marcaItem,
                        marca === marcaSeleccionada && styles.selected,
                        marca !== marcaSeleccionada && styles.marcaBlur
                      )}
                    >
                      <span className={styles.marcaNombre}>{marca}:</span>
                      <span>{(share * 100).toFixed(0)}%</span>
                    </div>
                  ))}
                </div>
              </div>

              <div className={styles.oportunidadesLista}>
                <h4>Oportunidades para {marcaSeleccionada} con Wecast:</h4>
                <ul>
                  <li>
                    <strong>Conecte con sus clientes fidelizados:</strong>{" "}
                    {formatNumber(resultados?.mercadoPorMarca[marcaSeleccionada]?.clientes || 0)} personas consumen Fast
                    Food y prefieren {marcaSeleccionada}.
                  </li>
                  <li>
                    <strong>Incremente el consumo</strong> de los clientes fidelizados.
                  </li>
                  <li>
                    <strong>Motive a consumir al 35% que no lo hace:</strong> {formatNumber(resultados?.noConsumidores)}{" "}
                    personas dijeron no tener el hábito de consumir Fast Food.
                  </li>
                  <li>
                    <strong>Delivery:</strong> 77% de la audiencia utiliza APP de delivery con alta frecuencia semanal.
                  </li>
                  <li>
                    <strong>Hijos:</strong> 33% tiene entre 5 y 10 años.
                  </li>
                  <li>
                    <strong>Mejore su participación de mercado:</strong>{" "}
                    {formatNumber(
                      resultados?.consumidores - (resultados?.mercadoPorMarca[marcaSeleccionada]?.clientes || 0)
                    )}{" "}
                    personas sí tienen el hábito pero prefieren otras cadenas.
                  </li>
                </ul>
              </div>

              <div className={styles.escenarios}>
                <h4>Escenario de captación para {marcaSeleccionada}</h4>
                {(() => {
                  const oportunidad = calcularOportunidadMarca(marcaSeleccionada, porcentajeCaptacion);
                  return (
                    <div className={styles.escenarioItem}>
                      <div className={styles.escenarioResultadoRow}>
                        <span className={styles.escenarioLabel}>Share que podría ganar:</span>
                        <select
                          value={porcentajeCaptacion}
                          onChange={(e) => setPorcentajeCaptacion(Number(e.target.value))}
                          className="form-select form-select-sm"
                          style={{ width: "100px" }}
                        >
                          {ESCENARIOS_CAPTACION.map((p) => (
                            <option key={p} value={p}>
                              +{p}%
                            </option>
                          ))}
                        </select>
                        <span>
                          Clientes adicionales:{" "}
                          <span className={styles.valor}>+{formatNumber(oportunidad.clientesGanados)}</span>
                        </span>
                      </div>
                      <div className={styles.escenarioValores}>
                        <div>
                          <span className={styles.valorLabel}>Mercado mensual ganado:</span>
                          <span className={styles.valorDestacado}>
                            {formatLocal(convertCLP(oportunidad.mercadoGanadoMensual))}
                          </span>
                        </div>
                        <div>
                          <span className={styles.valorLabel}>Mercado anual ganado:</span>
                          <span className={styles.valor}>
                            {formatLocal(convertCLP(oportunidad.mercadoGanadoAnual))}
                          </span>
                        </div>
                      </div>
                    </div>
                  );
                })()}
              </div>
            </div>
            <div className={styles.modalFooter}>
              <button className={styles.modalBtn} onClick={() => setShowOportunidadModal(false)}>
                Cerrar
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default FastFood;
